import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CompareProducts } from '@iheartjane/menu';
import { useMobileMediaQuery } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import { useMemo } from 'react';
import { Outlet } from 'react-router';

import { SearchProvider } from '@jane/search/hooks';
import {
  EmptyMenuProductGrid,
  PageLayout,
} from '@jane/shared-ecomm/components';
import {
  SpecialsProvider,
  useBrandSpecialsContext,
  useMenu,
  useSpecials,
} from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';
import { getMenuTabs } from '@jane/shared-ecomm/util';
import { useJaneUser } from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';

import { MenuBanners } from './MenuComponents/MenuBanners/MenuBanners';
import { MenuHeader } from './MenuComponents/MenuHeader/MenuHeader';
import { MenuResetLocationLink } from './MenuComponents/MenuResetLocationLink/MenuResetLocationLink';
import { MenuSeo } from './MenuComponents/MenuSeo/MenuSeo';
import { MenuSpecialBundleEnticement } from './MenuComponents/MenuSpecialBundleEnticement/MenuSpecialBundleEnticement';

const MenuInner = () => {
  const isMobile = useMobileMediaQuery({});

  const {
    appInfo: { appMode },
    facets,
    store,
  } = useMenu();
  const shouldShowGold = useShouldShowGold({
    store: { id: store.id, state: store.state },
  });

  const { specialIds, specials } = useSpecials();
  const { brandSpecials } = useBrandSpecialsContext();

  const hasVisibleSpecials = specials?.some(
    (special) => special.hidden_from_page !== true
  );

  const specialsPage =
    (store.specials_row_configs?.enabled ||
      store.custom_rows?.includes('sale')) &&
    Boolean(specialIds?.length) &&
    hasVisibleSpecials;

  const menuTabs = useMemo(
    () =>
      getMenuTabs({
        facets,
        isMarketplace: appMode === 'default',
        menuTabs: store.menu_tabs,
        specialsPage,
        showGoldTab: shouldShowGold && brandSpecials?.length > 0,
      }),
    [
      appMode,
      brandSpecials?.length,
      facets,
      shouldShowGold,
      specialsPage,
      store.menu_tabs,
    ]
  );

  return (
    <>
      <MenuSeo />
      <MenuSpecialBundleEnticement />
      <MenuHeader tabs={menuTabs} />
      <PageLayout id="content" py={isMobile ? 24 : 48} overflowX="visible">
        <MenuResetLocationLink />
        <MenuBanners tabs={menuTabs} />
        <EmptyMenuProductGrid />
        <Outlet />
      </PageLayout>
      <CompareProducts.Bar />
      <CompareProducts.Modal isMobile={isMobile} />
    </>
  );
};

export const Menu = () => {
  const { searchStateWithInitialQuery, onSearchStateChange, store } = useMenu();
  const compareEnabled = useFlag(FLAGS.c1CompareProducts);
  const { data: janeUser } = useJaneUser();

  const compareEventProperties = () => ({
    storeId: String(store.id),
    storeName: store.name,
    url: window.location.href,
    userType: (janeUser?.user?.authenticated ? 'registered' : 'guest') as
      | 'guest'
      | 'registered',
  });

  return (
    <SpecialsProvider fetchSpecialsRow>
      <SearchProvider
        indexPrefix="menu-products-"
        initialSearchState={searchStateWithInitialQuery}
        onSearchStateChange={onSearchStateChange}
      >
        <CompareProducts
          enabled={compareEnabled}
          onActiveChange={(isActive) => {
            if (!isActive) return;
            track({
              event: EventNames.ClickedCompare,
              ...compareEventProperties(),
            });
          }}
          onOpenModal={(productCount) =>
            track({
              event: EventNames.OpenedCompareModal,
              productCount,
              ...compareEventProperties(),
            })
          }
          onCloseModal={(productCount) =>
            track({
              event: EventNames.ClosedCompareModal,
              method: 'dismissed',
              productCount,
              ...compareEventProperties(),
            })
          }
          onClearAll={(productCount) =>
            track({
              event: EventNames.ClosedCompareModal,
              method: 'cleared_all',
              productCount,
              ...compareEventProperties(),
            })
          }
        >
          <MenuInner />
        </CompareProducts>
      </SearchProvider>
    </SpecialsProvider>
  );
};
